import { FC, useState } from 'react';
import { Modal, Button } from '@GxT-Team/gxt-components';
import { IQueriesModal } from './types';
import { FieldValues, useForm } from 'react-hook-form';
import QueryModalFields from './QueriesModalFields';
import { useDataSource } from '../../../../../services/api/dataSource/useDataSourceAPI';
import { generateFullHTMLTableString } from '../tableHtml';
import { useCatalog } from '../../../../../services/api/catalog/useCatalog';
import { useMappingSessionAPI } from '../../../../../services/api/mappingSession/useMappingSessionAPI';
import { useNavigate } from 'react-router-dom';
import { AddSourcesPreviousAction } from '../../../../catalog/util/constants';
import { documentNameFormat } from '../documentNameFormat';
import { DataSourceObj } from '../../../../../services/api/dataSource/dataSource.types';
import { DataItem } from '../../../types';
import { notifyError } from '../../../../../helpers/utils';

type ProcessQueryRequest = DataSourceObj & {
  val: Array<Record<string, unknown>>;
};

const QueriesModal: FC<IQueriesModal> = ({
  isOpen,
  handleClose,
  queries,
  sessionId,
  previousAction,
  openAuthoringModal,
  onSourceAdded,
}) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    reset,
  } = useForm({
    mode: 'onChange',
  });
  const navigate = useNavigate();
  const { processQuery } = useDataSource();
  const { addSourceFile } = useMappingSessionAPI();
  const { addToCatalog } = useCatalog();
  const [tableHeader, setIsTableHeader] = useState<string[]>([]);
  const [tableBody, setIsTableBody] = useState<Array<Record<string, string>>>([]);
  const [isPreviewData, setIsPreviewData] = useState<boolean>(false);
  const { id: queryId, name, type, sqlQuery } = queries || {};

  const isDisabled = addSourceFile.isPending || addToCatalog.isPending || processQuery.isPending;

  const handleQueryVariables = async (data: FieldValues) => {
    setIsPreviewData(true);
    setIsTableHeader([]);
    setIsTableBody([]);

    const resultArray = Object.entries(data).map(([key, value]) => ({
      [key]: value,
    }));

    if (!name || !type || !sqlQuery) {
      notifyError('Query configuration is incomplete');
      return;
    }

    const finalQueryObj: ProcessQueryRequest = {
      name,
      type,
      sqlQuery,
      val: resultArray,
    };

    const res = await processQuery.mutateAsync(finalQueryObj);
    if (!res?.data?.data.length) {
      notifyError('Data Not Found');
    }
    setIsTableHeader(res?.data?.data?.[0] ? Object.keys(res?.data?.data[0]) : []);
    setIsTableBody(res?.data?.data ?? []);
    return res;
  };

  const handleAddToCataLog = async () => {
    let htmlString = '';
    const variablesData = getValues();
    if (!isPreviewData) {
      const res = await handleQueryVariables(variablesData);
      setIsPreviewData(false);
      setIsTableHeader([]);
      setIsTableBody([]);
      if (!res?.data?.data.length) {
        return;
      }

      htmlString = generateFullHTMLTableString(Object.keys(res?.data?.data[0]), res?.data?.data);
    } else {
      htmlString = generateFullHTMLTableString(tableHeader, tableBody);
    }

    const formData = new FormData();
    formData.append('value', htmlString);
    formData.append('title', `${name} - (${documentNameFormat(variablesData as DataItem)})`);
    formData.append('documentType', 'Source');
    formData.append('documentName', `${name} - (${documentNameFormat(variablesData as DataItem)})`);
    formData.append('dataSourceId', `${queryId}`);

    addToCatalog.mutateAsync({ params: formData, asyncProcessing: true }).then((res) => {
      const catalogId =
        'catalogData' in res.data ? res.data.catalogData?.id : res.data.job?.result?.catalogId;

      if (!catalogId) {
        notifyError('Catalog upload is still processing. Please try again shortly.');
        return;
      }

      onSourceAdded?.(String(catalogId));
      if (previousAction === AddSourcesPreviousAction.AUTHORING_REQUEST) {
        handleClose();
        openAuthoringModal();
        setIsTableHeader([]);
        setIsTableBody([]);
      } else {
        addSourceFile
          .mutateAsync({
            sourceFileIds: [Number(catalogId)],
            sessionId,
            queryId,
          })
          .then(() => {
            navigate(`/mapping-sessions/${sessionId}`);
          });
      }
    });
  };

  const closeModalHandler = () => {
    reset();
    setIsTableHeader([]);
    setIsTableBody([]);
    setIsPreviewData(false);
    handleClose();
  };

  return (
    <Modal isOpen={isOpen} maxWidth="80rem" maxHeight="30.4rem" onClose={handleClose}>
      <Modal.ModalHeader>
        <Modal.ModalHeaderHeading>Source Details</Modal.ModalHeaderHeading>
        <Modal.ModalHeaderBody>
          <QueryModalFields
            control={control}
            errors={errors}
            dataSourceVariables={queries?.dataSourceVariables}></QueryModalFields>
        </Modal.ModalHeaderBody>
      </Modal.ModalHeader>
      <Button variant="primary" onClick={handleSubmit(handleQueryVariables)}>
        Preview Data
      </Button>
      {isPreviewData && tableHeader.length > 0 ? (
        <div className="overflow-x-auto max-w-full">
          <table className="min-w-full table-auto border-separate border-spacing-0 shadow-md rounded-lg overflow-hidden">
            <thead className="sticky top-0 z-10">
              <tr>
                {tableHeader?.map((head, index) => (
                  <th
                    key={head}
                    className={`${index !== tableHeader.length - 1 ? 'border-r border-white/35' : ''} px-4 py-3 text-left text-sm font-extrabold tracking-wide bg-primary_bg_color text-white`}>
                    {head}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white">
              {tableBody?.length > 0 ? (
                tableBody?.map((body, rowIndex) => (
                  <tr key={rowIndex} className="hover:bg-gray-50">
                    {tableHeader?.map((head) => (
                      <td key={`${rowIndex} - ${head}`} className="px-4 py-2 border-b">
                        {body[head]}
                      </td>
                    ))}
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={tableHeader.length} className="px-4 py-2 text-center">
                    No data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div>{null}</div>
      )}
      <Modal.ModalFooter className="!justify-end">
        <Button variant="primary" onClick={handleAddToCataLog} disabled={isDisabled}>
          Add
        </Button>
        <Button variant="secondary" onClick={closeModalHandler}>
          Cancel
        </Button>
      </Modal.ModalFooter>
    </Modal>
  );
};

export default QueriesModal;
